import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { intelliTrackClient } from '../lib/supabase'
import { UserProfile } from '../types/database'
import { AircraftMakesService } from '../services/intellijetnet/aircraft-makes.service'
import { AircraftModelsService } from '../services/intellijetnet/aircraft-models.service'
import { AircraftMake, AircraftModel } from '../types/intellijetnet'
import { capitalizeMakeName, formatModelName } from '../utils/formatters'

export default function MarketAssignments() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  
  const [user, setUser] = useState<UserProfile | null>(null)
  const [makes, setMakes] = useState<AircraftMake[]>([])
  const [selectedMakes, setSelectedMakes] = useState<Set<number>>(new Set())
  const [models, setModels] = useState<AircraftModel[]>([])
  const [modelsByMake, setModelsByMake] = useState<Map<number, AircraftModel[]>>(new Map())
  const [selectedModels, setSelectedModels] = useState<Set<number>>(new Set())
  const [allSelectedModelDetails, setAllSelectedModelDetails] = useState<Map<number, AircraftModel>>(new Map())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (userId) {
      fetchUserAndData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  useEffect(() => {
    // Fetch details for selected models whenever the selection changes
    if (selectedModels.size > 0) {
      // Ensure model IDs are numbers
      const modelIds = Array.from(selectedModels).map(id => typeof id === 'string' ? parseInt(id, 10) : id)
      fetchSelectedModelDetails(modelIds)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModels])

  useEffect(() => {
    if (selectedMakes.size > 0) {
      fetchModelsForSelectedMakes()
    } else {
      setModels([])
      setModelsByMake(new Map())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMakes])

  const fetchUserAndData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch user profile
      const { data: userData, error: userError } = await intelliTrackClient
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (userError) throw userError
      setUser(userData)
      
      // Fetch user's market assignments from the market_assignments table
      const { data: assignments, error: assignmentsError } = await intelliTrackClient
        .from('market_assignments')
        .select('model_id')
        .eq('user_id', userId)
      
      if (assignmentsError) throw assignmentsError
      
      // Set selected models from assignments
      const modelIds = (assignments || []).map(a => a.model_id)
      setSelectedModels(new Set(modelIds))

      // Fetch all makes
      const makesData = await AircraftMakesService.getAll()
      setMakes(makesData)
    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const fetchModelsForSelectedMakes = async () => {
    try {
      const allModels: AircraftModel[] = []
      const newModelsByMake = new Map<number, AircraftModel[]>()
      
      for (const makeId of selectedMakes) {
        const modelsData = await AircraftModelsService.getByMakeId(makeId)
        allModels.push(...modelsData)
        newModelsByMake.set(makeId, modelsData)
      }
      
      setModels(allModels)
      setModelsByMake(newModelsByMake)
      
      // Update allSelectedModelDetails with new models
      const updatedDetails = new Map(allSelectedModelDetails)
      allModels.forEach(model => {
        updatedDetails.set(model.model_id, model)
      })
      setAllSelectedModelDetails(updatedDetails)
    } catch (err: any) {
      setError(err.message || 'Failed to load models')
    }
  }

  const fetchSelectedModelDetails = async (modelIds: number[]) => {
    try {
      if (modelIds.length === 0) return
      
      const { data } = await AircraftModelsService.query()
        .in('model_id', modelIds)
        .execute<AircraftModel>()
      
      // Merge with existing details
      setAllSelectedModelDetails(prev => {
        const newMap = new Map(prev)
        data.forEach(model => {
          newMap.set(model.model_id, model)
        })
        return newMap
      })
    } catch (err: any) {
      // Failed to fetch selected model details
    }
  }

  const handleModelToggle = (modelId: number) => {
    const newSelected = new Set(selectedModels)
    if (newSelected.has(modelId)) {
      newSelected.delete(modelId)
    } else {
      newSelected.add(modelId)
    }
    setSelectedModels(newSelected)
  }

  const handleMakeToggle = (makeId: number) => {
    const newSelected = new Set(selectedMakes)
    if (newSelected.has(makeId)) {
      newSelected.delete(makeId)
      // Remove all models for this make from selection
      const makeModels = modelsByMake.get(makeId) || []
      const modelIdsToRemove = new Set(makeModels.map(m => m.model_id))
      const newSelectedModels = new Set(Array.from(selectedModels).filter(id => !modelIdsToRemove.has(id)))
      setSelectedModels(newSelectedModels)
    } else {
      newSelected.add(makeId)
    }
    setSelectedMakes(newSelected)
  }

  const handleSelectAllModels = () => {
    const allModelIds = models.map(m => m.model_id)
    setSelectedModels(new Set([...selectedModels, ...allModelIds]))
  }

  const handleDeselectAllModels = () => {
    const modelIdsToRemove = new Set(models.map(m => m.model_id))
    const newSelected = new Set(Array.from(selectedModels).filter(id => !modelIdsToRemove.has(id)))
    setSelectedModels(newSelected)
  }

  const handleSelectAllModelsForMake = (makeId: number) => {
    const makeModels = modelsByMake.get(makeId) || []
    const modelIds = makeModels.map(m => m.model_id)
    setSelectedModels(new Set([...selectedModels, ...modelIds]))
  }

  const handleDeselectAllModelsForMake = (makeId: number) => {
    const makeModels = modelsByMake.get(makeId) || []
    const modelIdsToRemove = new Set(makeModels.map(m => m.model_id))
    const newSelected = new Set(Array.from(selectedModels).filter(id => !modelIdsToRemove.has(id)))
    setSelectedModels(newSelected)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Get current assignments
      const { data: currentAssignments, error: fetchError } = await intelliTrackClient
        .from('market_assignments')
        .select('model_id')
        .eq('user_id', userId)
      
      if (fetchError) throw fetchError
      
      const currentModelIds = new Set((currentAssignments || []).map(a => a.model_id))
      const selectedModelIds = Array.from(selectedModels)
      
      // Find models to add and remove
      const modelsToAdd = selectedModelIds.filter(id => !currentModelIds.has(id))
      const modelsToRemove = Array.from(currentModelIds).filter(id => !selectedModels.has(id))
      
      // Delete removed assignments
      if (modelsToRemove.length > 0) {
        const { error: deleteError } = await intelliTrackClient
          .from('market_assignments')
          .delete()
          .eq('user_id', userId)
          .in('model_id', modelsToRemove)
        
        if (deleteError) throw deleteError
      }
      
      // Add new assignments
      if (modelsToAdd.length > 0) {
        const newAssignments = modelsToAdd.map(model_id => ({
          user_id: userId,
          model_id
        }))
        
        const { error: insertError } = await intelliTrackClient
          .from('market_assignments')
          .insert(newAssignments)
        
        if (insertError) throw insertError
      }

      setSuccess('Market assignments updated successfully')
      
      // Redirect back to users page after a short delay
      setTimeout(() => {
        navigate('/users')
      }, 1500)
    } catch (err: any) {
      setError(err.message || 'Failed to save market assignments')
    } finally {
      setSaving(false)
    }
  }

  const getAssignedModelsInfo = () => {
    // Get all models that are currently assigned
    const assignedModelIds = Array.from(selectedModels)
    return assignedModelIds.length
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">Loading...</div>
        </div>
      </Layout>
    )
  }

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">User not found</div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <button
              onClick={() => navigate('/users')}
              className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
            >
              ← Back to Users
            </button>
            <h1 className="text-3xl font-bold text-purple-950">Market Assignments</h1>
            <p className="mt-2 text-gray-600">
              Managing market assignments for: <span className="font-semibold">{user.display_name || 'No display name'}</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Currently assigned to {getAssignedModelsInfo()} models
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-700">{success}</div>
            </div>
          )}

          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Select Aircraft Makes</h2>
            <p className="text-sm text-gray-600 mb-4">Select one or more aircraft makes to view their models</p>
            <div className="flex flex-wrap gap-2">
              {makes.map((make) => (
                <label
                  key={make.make_id}
                  className="cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedMakes.has(make.make_id)}
                    onChange={() => handleMakeToggle(make.make_id)}
                    className="sr-only"
                  />
                  <span
                    className={`
                      inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium
                      transition-all duration-200 ease-in-out
                      ${selectedMakes.has(make.make_id)
                        ? 'bg-purple-100 text-purple-800 ring-2 ring-purple-500 ring-opacity-50'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                    `}
                  >
                    {capitalizeMakeName(make.make_name)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {selectedMakes.size > 0 && models.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Models for Selected Makes ({models.length} total)
                </h2>
                <div className="space-x-2">
                  <button
                    onClick={handleSelectAllModels}
                    className="text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-1.5 rounded-full transition-colors duration-150 font-medium"
                  >
                    Select All Models
                  </button>
                  <button
                    onClick={handleDeselectAllModels}
                    className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-1.5 rounded-full transition-colors duration-150 font-medium"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                {Array.from(selectedMakes).map(makeId => {
                  const make = makes.find(m => m.make_id === makeId)
                  const makeModels = modelsByMake.get(makeId) || []
                  if (makeModels.length === 0) return null
                  
                  return (
                    <div key={makeId} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium text-gray-900">
                          {make ? capitalizeMakeName(make.make_name) : ''} ({makeModels.length} models)
                        </h3>
                        <div className="space-x-2">
                          <button
                            onClick={() => handleSelectAllModelsForMake(makeId)}
                            className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1 rounded-full transition-colors duration-150"
                          >
                            Select All
                          </button>
                          <button
                            onClick={() => handleDeselectAllModelsForMake(makeId)}
                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-colors duration-150"
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {makeModels.map((model) => (
                          <label
                            key={model.model_id}
                            className="cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedModels.has(model.model_id)}
                              onChange={() => handleModelToggle(model.model_id)}
                              className="sr-only"
                            />
                            <span
                              className={`
                                inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
                                transition-all duration-200 ease-in-out
                                ${selectedModels.has(model.model_id)
                                  ? 'bg-purple-100 text-purple-800 ring-2 ring-purple-500 ring-opacity-50'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }
                              `}
                            >
                              {formatModelName(model.model)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Selected Models Summary</h2>
            <div className="mb-6">
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div>
                  <p className="text-lg font-medium text-purple-900">
                    {selectedModels.size} {selectedModels.size === 1 ? 'model' : 'models'} selected
                  </p>
                  <p className="text-sm text-purple-700">
                    {(() => {
                      // Count unique makes from selected models
                      const uniqueMakes = new Set<number>()
                      selectedModels.forEach(modelId => {
                        const numericId = typeof modelId === 'string' ? parseInt(modelId, 10) : modelId
                        const model = allSelectedModelDetails.get(numericId)
                        if (model && model.make_id) {
                          uniqueMakes.add(model.make_id)
                        }
                      })
                      const makeCount = uniqueMakes.size
                      return `Across ${makeCount} ${makeCount === 1 ? 'make' : 'makes'}`
                    })()}
                  </p>
                </div>
                <svg className="h-10 w-10 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              {selectedModels.size > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Models:</h3>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(selectedModels).map(modelId => {
                      // Ensure modelId is a number when looking up
                      const numericId = typeof modelId === 'string' ? parseInt(modelId, 10) : modelId
                      const model = allSelectedModelDetails.get(numericId)
                      if (!model) {
                        return (
                          <span
                            key={modelId}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            Model ID: {modelId} (loading...)
                          </span>
                        )
                      }
                      
                      const make = makes.find(m => m.make_id === model.make_id)
                      return (
                        <span
                          key={model.model_id}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                        >
                          {make ? capitalizeMakeName(make.make_name) : 'Unknown'} {formatModelName(model.model)}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => navigate('/users')}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Assignments'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}